import { useCallback, useState } from "react";
import { logOutAction, selectIsSsoLogin, setLastVisitedLink } from "redux/authSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { logoutWithSso } from "screens/Auth/authSession";
import viewpaths from "global/constants/ViewPathConstants";
import history from "utils/history";

interface LogoutController {
  isSsoLogin: boolean;
  isConfirmOpen: boolean;
  isLoggingOut: boolean;
  requestLogout: () => void;
  confirmLogout: (alsoSignOutSso: boolean) => Promise<void>;
  cancelLogout: () => void;
}

const endSessionLocally = (dispatch: ReturnType<typeof useAppDispatch>) => {
  const path = history.location.pathname + history.location.search;
  dispatch(logOutAction({}));
  dispatch(setLastVisitedLink(path));
  history.push(viewpaths.landingViewPath);
};

export function useLogout(): LogoutController {
  const dispatch = useAppDispatch();
  const isSsoLogin = useAppSelector(selectIsSsoLogin);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const requestLogout = useCallback(() => {
    if (isSsoLogin) {
      setIsConfirmOpen(true);
      return;
    }
    endSessionLocally(dispatch);
  }, [isSsoLogin, dispatch]);

  const confirmLogout = useCallback(
    async (alsoSignOutSso: boolean) => {
      if (!alsoSignOutSso) {
        setIsConfirmOpen(false);
        endSessionLocally(dispatch);
        return;
      }
      setIsLoggingOut(true);
      const redirectingToIdp = await logoutWithSso(dispatch);
      if (!redirectingToIdp) {
        setIsLoggingOut(false);
        setIsConfirmOpen(false);
      }
    },
    [dispatch],
  );

  const cancelLogout = useCallback(() => setIsConfirmOpen(false), []);

  return {
    isSsoLogin,
    isConfirmOpen,
    isLoggingOut,
    requestLogout,
    confirmLogout,
    cancelLogout,
  };
}
